// src/components/TrajectoryPage.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { getAmmoForProfile } from '../data/ammoDB'
import { useAuth } from '../lib/AuthContext'
import { autoFillConditions, windDirectionLabel } from '../lib/weather'
import { calculateDropTable, calcMPBR } from '../lib/ballistics'

const ZERO_CHIPS = [25, 37, 50, 100, 200, 300]

function WeatherInput({ label, value, onChange, unit, min, max, step = 1 }) {
  return (
    <div style={styles.weatherCell}>
      <div style={styles.weatherLabel}>{label}</div>
      <div style={styles.weatherInputWrap}>
        <input type="number" style={styles.weatherInput} value={value} min={min} max={max} step={step}
          onChange={e => onChange(parseFloat(e.target.value) || 0)} />
        <span style={styles.weatherUnit}>{unit}</span>
      </div>
    </div>
  )
}

function DropRow({ row, zeroYards, minExpVel }) {
  const isZero    = row.distance === zeroYards
  const dropColor = row.drop > 0 ? '#4caf7d' : row.drop < 0 ? '#e05c5c' : '#c8a96e'
  const isLow     = minExpVel && row.velocity < minExpVel
  return (
    <tr style={{ background: isZero ? 'rgba(200,169,110,0.06)' : 'transparent' }}>
      <td style={styles.tdDist}>{row.distance} yd{isZero && <span style={styles.zeroBadge}>ZERO</span>}</td>
      <td style={{ ...styles.td, color: dropColor, fontWeight: 600 }}>{row.drop > 0 ? '+' : ''}{row.drop.toFixed(1)}"</td>
      <td style={{ ...styles.td, color: isLow ? '#e05c5c' : '#f0ede8' }}>{row.velocity.toLocaleString()}</td>
      <td style={styles.td}>{row.energy.toLocaleString()}</td>
      <td style={{ ...styles.td, color: '#5b9bd5' }}>{row.windDrift > 0 ? '+' : ''}{row.windDrift.toFixed(1)}"</td>
    </tr>
  )
}

export default function TrajectoryPage({ activeProfile, onUpgrade }) {
  const { isPro } = useAuth()
  const [selectedAmmoId, setSelectedAmmoId] = useState('')
  const [ammoList, setAmmoList]             = useState([])
  const [tempF,      setTempF]      = useState(59)
  const [altFt,      setAltFt]      = useState(500)
  const [windMph,    setWindMph]    = useState(0)
  const [windAngle,  setWindAngle]  = useState(90)
  const [weatherLoading, setWeatherLoading] = useState(false)
  const [weatherError,   setWeatherError]   = useState('')
  const [weatherLive,    setWeatherLive]    = useState(false)
  const [locationName,   setLocationName]   = useState('')
  const [maxYards,  setMaxYards]  = useState(500)
  const [stepYards, setStepYards] = useState(25)
  const [dropTable, setDropTable] = useState([])
  const [mpbr,      setMpbr]      = useState(null)
  const [ethRange,  setEthRange]  = useState(null)
  const [computing, setComputing] = useState(false)
  const [searchQuery,   setSearchQuery]   = useState('')
  const [showDropdown,  setShowDropdown]  = useState(false)
  const [overrideZero,  setOverrideZero]  = useState(null)
  const calcTimer = useRef(null)
  const searchRef = useRef(null)

  const selectedAmmo = ammoList.find(a => a.id === selectedAmmoId)
  const activeZero = overrideZero ?? activeProfile?.zero_distance ?? 100

  const filteredRounds = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return ammoList
    return ammoList.filter(a => {
      if (a.name?.toLowerCase().includes(q)) return true
      if (String(a.grains).includes(q)) return true
      if (a.manufacturer?.toLowerCase().includes(q)) return true
      if (a.bullet_type?.toLowerCase().includes(q)) return true
      if (a.calibers?.some(c => c.toLowerCase().includes(q))) return true
      return false
    })
  }, [searchQuery, ammoList])

  function pickRound(a) {
    setSelectedAmmoId(a.id)
    setSearchQuery('')
    setShowDropdown(false)
  }

  function clearSelection() {
    setSelectedAmmoId('')
    setSearchQuery('')
    setShowDropdown(true)
  }

  // Reset zero override when profile changes
  useEffect(() => { setOverrideZero(null) }, [activeProfile?.id])

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  function runCalculation(ammo, profile, opts) {
    if (!ammo || !profile) return
    if (calcTimer.current) clearTimeout(calcTimer.current)
    setComputing(true)
    calcTimer.current = setTimeout(() => {
      try {
        const params = {
          mv: ammo.corrected_mv || ammo.base_mv,
          bc: ammo.bc_g1,
          bulletWeightGr: ammo.grains,
          sightHeightIn: profile.sight_height,
          zeroYards: opts.zeroYards ?? profile.zero_distance,
          maxYards: opts.maxYards,
          stepYards: opts.stepYards,
          tempF: opts.tempF,
          altitudeFt: opts.altFt,
          windMph: opts.windMph,
          windAngleDeg: opts.windAngle,
        }
        const table = calculateDropTable(params)
        setDropTable(table)
        setMpbr(calcMPBR(params, 10))
        if (ammo.min_expansion_vel) {
          const eth = table.filter(r => r.velocity >= ammo.min_expansion_vel)
          setEthRange(eth.length > 0 ? eth[eth.length - 1].distance : 0)
        } else {
          setEthRange(null)
        }
      } catch(e) {
        console.error('Ballistic calc error:', e)
        setDropTable([])
      }
      setComputing(false)
    }, 0)
  }

  // Load ammo when profile changes
  useEffect(() => {
    if (!activeProfile) return
    const rounds = getAmmoForProfile(activeProfile, 'all')
    setAmmoList(rounds)
    if (rounds.length > 0) setSelectedAmmoId(rounds[0].id)
  }, [activeProfile])

  // Trigger calculation when inputs change — no calculate in deps
  useEffect(() => {
    if (!selectedAmmoId || !activeProfile || ammoList.length === 0) return
    const ammo = ammoList.find(a => a.id === selectedAmmoId)
    if (!ammo) return
    runCalculation(ammo, activeProfile, { maxYards, stepYards, tempF, altFt, windMph, windAngle, zeroYards: activeZero })
  }, [selectedAmmoId, ammoList, activeProfile, tempF, altFt, windMph, windAngle, maxYards, stepYards, activeZero]) // eslint-disable-line

  async function fetchLiveWeather() {
    setWeatherLoading(true)
    setWeatherError('')
    try {
      const conditions = await autoFillConditions()
      setTempF(conditions.tempF)
      setAltFt(conditions.altFt)
      setWindMph(conditions.windMph)
      setWindAngle(conditions.windAngle)
      setWeatherLive(true)
      try {
        const geoRes  = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${conditions.latitude}&lon=${conditions.longitude}&format=json`)
        const geoData = await geoRes.json()
        const city  = geoData.address?.city || geoData.address?.town || geoData.address?.county || ''
        const state = geoData.address?.state_code || geoData.address?.state || ''
        if (city || state) setLocationName(`${city}${city && state ? ', ' : ''}${state}`)
      } catch { /* optional */ }
    } catch (err) {
      setWeatherError(err.code === 1 ? 'Location access denied. Enable location permissions.' : 'Could not fetch weather. Check your connection.')
      setWeatherLive(false)
    }
    setWeatherLoading(false)
  }

  if (!activeProfile) {
    return (
      <div style={styles.noProfile}>
        <div style={styles.npIcon}>◉</div>
        <div style={styles.npTitle}>No rifle profile selected</div>
        <div style={styles.npSub}>Go to Rifles tab and select a profile first</div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.profilePill}>
        <span style={styles.pillDot} />
        <span style={styles.pillText}>{activeProfile.nickname} · {activeProfile.caliber} · {activeProfile.barrel_length}" · Zero: {activeProfile.zero_distance}yd</span>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Select Round</div>
        <div ref={searchRef} style={styles.searchWrap}>
          {selectedAmmo ? (
            <div style={styles.selectedPill}>
              <span style={styles.selectedCheck}>✓</span>
              <div style={styles.selectedTextWrap}>
                <div style={styles.selectedName}>{selectedAmmo.name}</div>
                <div style={styles.selectedMeta}>{selectedAmmo.grains}gr · {selectedAmmo.corrected_mv || selectedAmmo.base_mv}fps · {selectedAmmo.manufacturer}</div>
              </div>
              <button style={styles.searchClearBtn} onClick={clearSelection} aria-label="Clear selection">✕</button>
            </div>
          ) : (
            <input
              type="text"
              style={styles.searchInput}
              value={searchQuery}
              placeholder="Search rounds... (e.g. '66gr', 'ELD-X', 'Federal')"
              onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true) }}
              onFocus={() => setShowDropdown(true)}
            />
          )}
          {showDropdown && !selectedAmmo && (
            <div style={styles.dropdown}>
              {filteredRounds.length === 0 ? (
                <div style={styles.dropdownEmpty}>No rounds match your search</div>
              ) : (
                filteredRounds.map(a => (
                  <button key={a.id} style={styles.dropdownItem} onClick={() => pickRound(a)}>
                    <div style={styles.dropdownItemName}>{a.name}</div>
                    <div style={styles.dropdownItemMeta}>{a.grains}gr · {a.corrected_mv || a.base_mv}fps · {a.manufacturer}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selectedAmmo && (
        <div style={styles.roundSummary}>
          <div style={styles.summaryItem}><div style={styles.summaryLabel}>MV (corrected)</div><div style={styles.summaryVal}>{selectedAmmo.corrected_mv || selectedAmmo.base_mv}<span style={styles.summaryUnit}> fps</span></div></div>
          <div style={styles.summaryItem}><div style={styles.summaryLabel}>BC (G1)</div><div style={styles.summaryVal}>{selectedAmmo.bc_g1}</div></div>
          <div style={styles.summaryItem}><div style={styles.summaryLabel}>Bullet Wt</div><div style={styles.summaryVal}>{selectedAmmo.grains}<span style={styles.summaryUnit}> gr</span></div></div>
          <div style={styles.summaryItem}><div style={styles.summaryLabel}>Zero</div><div style={styles.summaryVal}>{activeZero}<span style={styles.summaryUnit}> yd</span></div></div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Calculate for zero distance</div>
        <div style={styles.zeroInputRow}>
          <input
            type="number"
            style={styles.zeroInput}
            value={activeZero}
            min={1}
            max={1000}
            onChange={e => {
              const v = parseInt(e.target.value, 10)
              setOverrideZero(Number.isFinite(v) && v > 0 ? Number(v) : null)
            }}
          />
          <span style={styles.zeroUnit}>yd</span>
        </div>
        <div style={styles.zeroChips}>
          {ZERO_CHIPS.map(y => {
            const active = Number(activeZero) === y
            return (
              <button
                key={y}
                style={{ ...styles.zeroChip, ...(active ? styles.zeroChipActive : {}) }}
                onClick={() => setOverrideZero(y)}
              >
                {y}yd
              </button>
            )
          })}
        </div>
        {!ZERO_CHIPS.includes(Number(activeZero)) && (
          <div style={styles.customZeroHint}>Custom zero: {activeZero}yd active</div>
        )}
        <div style={styles.zeroNote}>
          Profile zero: {activeProfile.zero_distance}yd
          {overrideZero != null && overrideZero !== activeProfile.zero_distance && (
            <>
              {' — '}
              <button style={styles.zeroResetLink} onClick={() => setOverrideZero(null)}>tap to reset</button>
            </>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabelRow}>
          <div style={styles.sectionLabel}>
            Conditions
            {weatherLive && <span style={styles.liveTag}><span style={styles.liveDot} />LIVE {locationName ? `· ${locationName}` : ''}</span>}
          </div>
          <button style={{ ...styles.weatherBtn, opacity: weatherLoading ? 0.6 : 1 }} onClick={fetchLiveWeather} disabled={weatherLoading}>
            {weatherLoading ? 'Fetching...' : '⌖ Auto-fill from GPS'}
          </button>
        </div>
        {weatherError && <div style={styles.weatherError}>{weatherError}</div>}
        <div style={styles.weatherGrid}>
          <WeatherInput label="Temp"     value={tempF}    onChange={setTempF}    unit="°F"  min={-40} max={120}   />
          <WeatherInput label="Altitude" value={altFt}    onChange={setAltFt}    unit="ft"  min={0}   max={15000} step={100} />
          <WeatherInput label="Wind"     value={windMph}  onChange={setWindMph}  unit="mph" min={0}   max={50}    />
          <WeatherInput label={`Wind Dir ${windDirectionLabel(windAngle)}`} value={windAngle} onChange={setWindAngle} unit="°" min={0} max={360} step={45} />
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Range Settings</div>
        <div style={styles.rangeRow}>
          <div style={styles.rangeField}>
            <div style={styles.rangeLabel}>Max Range</div>
            <select style={styles.rangeSelect} value={maxYards} onChange={e => setMaxYards(parseInt(e.target.value))}>
              {[200,300,400,500,600,700,800,1000].map(y => <option key={y} value={y}>{y} yds</option>)}
            </select>
          </div>
          <div style={styles.rangeField}>
            <div style={styles.rangeLabel}>Step Size</div>
            <select style={styles.rangeSelect} value={stepYards} onChange={e => setStepYards(parseInt(e.target.value))}>
              {[10,25,50,100].map(y => <option key={y} value={y}>Every {y} yds</option>)}
            </select>
          </div>
        </div>
      </div>

      {(mpbr || ethRange) && (
        <div style={styles.keyStats}>
          {mpbr > 0 && <div style={styles.keyStat}><div style={styles.keyStatLabel}>MPBR (10" vital)</div><div style={styles.keyStatVal}>{mpbr}<span style={styles.keyStatUnit}> yds</span></div></div>}
          {ethRange > 0 && <div style={styles.keyStat}><div style={styles.keyStatLabel}>Ethical Range</div><div style={styles.keyStatVal}>{ethRange}<span style={styles.keyStatUnit}> yds</span></div></div>}
          {selectedAmmo?.min_expansion_vel && <div style={styles.keyStat}><div style={styles.keyStatLabel}>Min Expansion</div><div style={styles.keyStatVal}>{selectedAmmo.min_expansion_vel}<span style={styles.keyStatUnit}> fps</span></div></div>}
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Drop Table{computing && <span style={styles.computing}> calculating...</span>}</div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, textAlign: 'left' }}>Range</th>
                <th style={styles.th}>Drop</th>
                <th style={styles.th}>Vel (fps)</th>
                <th style={styles.th}>Energy</th>
                <th style={styles.th}>Wind</th>
              </tr>
            </thead>
            <tbody>
              {(isPro ? dropTable : dropTable.slice(0, 3)).map(row => <DropRow key={row.distance} row={row} zeroYards={activeZero} minExpVel={selectedAmmo?.min_expansion_vel} />)}
            </tbody>
          </table>
          {!isPro && dropTable.length > 3 && (
            <div style={styles.paywall}>
              <div style={styles.paywallBlur}>
                {dropTable.slice(3, 6).map(row => (
                  <div key={row.distance} style={styles.paywallGhostRow}>
                    <span>{row.distance} yd</span>
                    <span>{row.drop > 0 ? '+' : ''}{row.drop.toFixed(1)}"</span>
                    <span>{row.velocity.toLocaleString()}</span>
                    <span>{row.energy.toLocaleString()}</span>
                    <span>{row.windDrift.toFixed(1)}"</span>
                  </div>
                ))}
              </div>
              <div style={styles.paywallOverlay}>
                <div style={styles.paywallLock}>🔒</div>
                <div style={styles.paywallTitle}>Upgrade to Pro to see full drop table</div>
                <div style={styles.paywallSub}>{dropTable.length - 3} more rows · all distances out to {dropTable[dropTable.length - 1]?.distance} yd</div>
                <button style={styles.paywallBtn} onClick={() => onUpgrade && onUpgrade()}>
                  ⚡ Upgrade to Pro →
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={styles.legend}>
          <span style={styles.legendItem}><span style={{ color: '#4caf7d' }}>+</span> Above zero</span>
          <span style={styles.legendItem}><span style={{ color: '#e05c5c' }}>−</span> Below zero</span>
          <span style={styles.legendItem}><span style={{ color: '#e05c5c' }}>Red vel</span> = below expansion threshold</span>
        </div>
      </div>

      <div style={styles.disclaimer}>
        G1 point-mass ballistic model. MV corrected for {activeProfile.barrel_length}" barrel. Weather via Open-Meteo. Verify at the range.
      </div>
    </div>
  )
}

const styles = {
  container:    { display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 24 },
  noProfile:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 30px', gap: 14 },
  npIcon:       { fontSize: 40, color: 'var(--accent)' },
  npTitle:      { fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' },
  npSub:        { fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', textAlign: 'center', lineHeight: 1.6 },
  profilePill:  { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' },
  pillDot:      { width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 },
  pillText:     { fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em' },
  section:         { padding: '14px 20px', borderBottom: '1px solid var(--border)' },
  sectionLabelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  sectionLabel:    { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Courier New, monospace', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 },
  computing:       { color: 'var(--accent)', fontSize: 9, fontFamily: 'Courier New, monospace' },
  liveTag:  { display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(76,175,125,0.12)', border: '1px solid rgba(76,175,125,0.25)', color: '#4caf7d', fontSize: 9, padding: '2px 8px', borderRadius: 4, fontFamily: 'Courier New, monospace' },
  liveDot:  { width: 5, height: 5, borderRadius: '50%', background: '#4caf7d', flexShrink: 0 },
  weatherBtn: { background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', color: 'var(--accent)', borderRadius: 7, padding: '7px 12px', fontSize: 11, fontFamily: 'Courier New, monospace', cursor: 'pointer', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  weatherError: { fontSize: 11, color: '#e05c5c', fontFamily: 'Courier New, monospace', marginBottom: 10, padding: '6px 10px', background: 'rgba(224,92,92,0.08)', borderRadius: 6 },
  weatherGrid:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  weatherCell:      { display: 'flex', flexDirection: 'column', gap: 5 },
  weatherLabel:     { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace' },
  weatherInputWrap: { display: 'flex', alignItems: 'center', gap: 6 },
  weatherInput:     { flex: 1, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 14, padding: '9px 10px', textAlign: 'right', outline: 'none' },
  weatherUnit:      { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', minWidth: 28 },
  roundSelect:  { width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 12, padding: '10px 12px' },
  searchWrap:        { position: 'relative' },
  searchInput:       { width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 13, padding: '11px 14px', outline: 'none', boxSizing: 'border-box' },
  searchClearBtn:    { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer', fontFamily: 'Courier New, monospace', padding: '4px 8px', flexShrink: 0 },
  selectedPill:      { display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-tertiary)', border: '1px solid rgba(200,169,110,0.3)', borderRadius: 8, padding: '10px 12px' },
  selectedCheck:     { color: '#c8a96e', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  selectedTextWrap:  { flex: 1, minWidth: 0 },
  selectedName:      { fontSize: 13, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  selectedMeta:      { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dropdown:          { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#141414', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  dropdownEmpty:     { padding: '14px', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', textAlign: 'center' },
  dropdownItem:      { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '10px 14px', cursor: 'pointer', fontFamily: 'Courier New, monospace' },
  dropdownItemName:  { fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 },
  dropdownItemMeta:  { fontSize: 10, color: 'var(--text-muted)', marginTop: 2 },
  zeroInputRow:      { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  zeroInput:         { flex: 1, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 14, padding: '9px 12px', textAlign: 'right', outline: 'none', maxWidth: 140 },
  zeroUnit:          { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace' },
  zeroChips:         { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
  zeroChip:          { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 999, padding: '6px 12px', fontSize: 11, fontFamily: 'Courier New, monospace', cursor: 'pointer', letterSpacing: '0.04em' },
  zeroChipActive:    { background: '#c8a96e', color: '#0a0a0a', borderColor: '#c8a96e', fontWeight: 700 },
  zeroNote:          { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em' },
  customZeroHint:    { fontSize: 11, color: 'var(--accent)', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em', marginBottom: 8, fontWeight: 600 },
  zeroResetLink:     { background: 'transparent', border: 'none', color: 'var(--accent)', fontSize: 10, fontFamily: 'Courier New, monospace', cursor: 'pointer', padding: 0, textDecoration: 'underline', letterSpacing: '0.04em' },
  roundSummary: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, padding: '0 20px 14px', borderBottom: '1px solid var(--border)' },
  summaryItem:  { background: 'var(--bg-secondary)', borderRadius: 7, padding: '8px 10px' },
  summaryLabel: { fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Courier New, monospace' },
  summaryVal:   { fontSize: 14, fontWeight: 600, color: 'var(--accent)', fontFamily: 'Courier New, monospace', marginTop: 3 },
  summaryUnit:  { fontSize: 9, color: 'var(--text-muted)' },
  rangeRow:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  rangeField:  { display: 'flex', flexDirection: 'column', gap: 5 },
  rangeLabel:  { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace' },
  rangeSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 13, padding: '9px 10px' },
  keyStats:     { display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' },
  keyStat:      { flex: 1, padding: '12px 16px', borderRight: '1px solid var(--border)', textAlign: 'center' },
  keyStatLabel: { fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Courier New, monospace', marginBottom: 4 },
  keyStatVal:   { fontSize: 18, fontWeight: 700, color: 'var(--accent)', fontFamily: 'Courier New, monospace' },
  keyStatUnit:  { fontSize: 10, color: 'var(--text-muted)' },
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-secondary)', position: 'relative' },
  paywall:        { position: 'relative', minHeight: 220 },
  paywallBlur:    { filter: 'blur(6px)', opacity: 0.55, padding: '4px 0', pointerEvents: 'none', userSelect: 'none' },
  paywallGhostRow:{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr 1fr 1fr', gap: 8, padding: '12px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, fontFamily: 'Courier New, monospace', color: '#888', textAlign: 'right' },
  paywallOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'linear-gradient(180deg, rgba(15,15,15,0.6) 0%, rgba(10,10,10,0.95) 60%)', padding: '20px' },
  paywallLock:    { fontSize: 24 },
  paywallTitle:   { fontSize: 14, fontWeight: 700, color: '#f0ede8', fontFamily: 'Georgia, serif', textAlign: 'center' },
  paywallSub:     { fontSize: 11, color: '#888', fontFamily: 'Courier New, monospace', textAlign: 'center' },
  paywallBtn:     { background: '#c8a96e', color: '#0a0a0a', border: 'none', borderRadius: 9, padding: '11px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '0.02em', marginTop: 4 },
  table:     { width: '100%', borderCollapse: 'collapse', minWidth: 320 },
  th:        { padding: '10px 12px', fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace', textAlign: 'right', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border)', fontWeight: 500 },
  tdDist:    { padding: '10px 12px', fontSize: 12, color: 'var(--accent)', fontFamily: 'Courier New, monospace', fontWeight: 600, borderBottom: '1px solid var(--border)' },
  td:        { padding: '10px 12px', fontSize: 12, fontFamily: 'Courier New, monospace', color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--border)' },
  zeroBadge: { fontSize: 8, background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent-border)', padding: '1px 5px', borderRadius: 3, letterSpacing: '0.06em', marginLeft: 6 },
  legend:    { display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' },
  legendItem:{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace' },
  disclaimer:{ padding: '12px 20px', fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', lineHeight: 1.6, borderTop: '1px solid var(--border)' },
}
